import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Check, FileText, LoaderCircle, Plus } from "lucide-react";
import type { PropertyIntake as Intake } from "../shared/intake";
import { api, jsonBody } from "./api";

const message = (error: unknown) => error instanceof Error ? error.message : "Please try again.";
const isPdf = (file: File) => file.type === "application/pdf" || (!file.type && /\.pdf$/i.test(file.name));

export function PropertyIntake({ onReview, onPublished }: { onReview: (intake: Intake) => void; onPublished: () => Promise<void> }) {
  const [items, setItems] = useState<Intake[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const reload = useCallback(async () => {
    const result = await api<{ items: Intake[] }>("/api/chunkz/intake");
    setItems(result.items);
  }, []);
  useEffect(() => { void reload().catch((err) => setError(message(err))); }, [reload]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const files = Array.from(form.querySelector<HTMLInputElement>('input[name="pdfs"]')?.files || []);
    if (files.length > 10 || files.some((file) => file.size > 15_000_000 || !isPdf(file))) {
      setError("Add up to 10 PDFs, each no larger than 15 MB."); return;
    }
    const links = String(data.get("links") || "").split(/\r?\n/).map((value) => value.trim()).filter(Boolean);
    setBusy(true); setError(""); setSuccess("");
    try {
      const created = await api<{ id: string }>("/api/chunkz/intake", {
        method: "POST", ...jsonBody({ title: data.get("title"), details: data.get("details"), links }),
      });
      for (const file of files) {
        await api(`/api/chunkz/intake/${created.id}/files`, {
          method: "POST", headers: { "content-type": "application/pdf", "x-file-name": encodeURIComponent(file.name) },
          body: file,
        });
      }
      form.reset();
      setSuccess("Submission saved. Review its details before publishing it to the buyer shortlist.");
      await reload();
      await onPublished();
    } catch (err) {
      setError(`${message(err)} If the submission appears below, its text and links were saved; check the PDF list before retrying.`);
      await reload().catch(() => {});
    } finally { setBusy(false); }
  };
  const addFiles = async (item: Intake, files: File[]) => {
    if (!files.length) return;
    if (item.files.length + files.length > 10 || files.some((file) => file.size > 15_000_000 || !isPdf(file))) {
      setError("Each submission can hold up to 10 PDFs, each no larger than 15 MB."); return;
    }
    setBusy(true); setError(""); setSuccess("");
    try {
      for (const file of files) await api(`/api/chunkz/intake/${item.id}/files`, {
        method: "POST", headers: { "content-type": "application/pdf", "x-file-name": encodeURIComponent(file.name) }, body: file,
      });
      await reload();
      setSuccess("PDFs added to the team submission.");
    } catch (err) { setError(message(err)); await reload().catch(() => {}); }
    finally { setBusy(false); }
  };
  const pending = items.filter((item) => item.status === "pending");
  return <section className="chunkz-intake" aria-label="Adding new properties">
    <div className="chunkz-intake-heading"><div><p className="eyebrow">VPS360 team workspace</p><h2>Adding new properties</h2><p>Share everything you have. Submissions stay with the team until a property is reviewed and published.</p></div><span>{pending.length} awaiting review</span></div>
    <p>Current buyer brief: Include Stanmore, Edgware and Maida Vale. Prioritise high-street footfall, frontage and at least 1,000 sq ft of usable space. Do not add Kilburn High Road or Wembley.</p>
    <form onSubmit={(event) => void submit(event)} className="chunkz-intake-form">
      <label>Property name or address<input name="title" maxLength={200} required placeholder="e.g. 42 High Road, NW London" /></label>
      <label>Details, messages and notes<textarea name="details" maxLength={40000} required rows={6} placeholder="Paste the agent's message, rent, size, use, availability and anything else you know." /></label>
      <label>Links · one per line<textarea name="links" rows={3} placeholder="https://example.com/listing" /></label>
      <label>PDF brochures or floor plans · up to 10 files, 15 MB each<input name="pdfs" type="file" accept="application/pdf,.pdf" multiple /></label>
      <button className="button button-primary" disabled={busy}>{busy ? <LoaderCircle className="spin" size={16} /> : <Plus size={16} />} Submit property information</button>
    </form>
    {error && <p className="form-error" role="alert">{error}</p>}
    {success && <p className="chunkz-intake-success" role="status"><Check size={16} /> {success}</p>}
    <div className="chunkz-intake-list"><h3>Team submissions</h3>{!items.length && <p>No submissions yet.</p>}{items.map((item) => <article key={item.id} className="chunkz-intake-item">
      <div><strong>{item.title}</strong><span>{item.status === "pending" ? "Awaiting review" : "Published"} · {item.createdBy} · {new Date(item.createdAt).toLocaleDateString("en-GB")}</span></div>
      <details><summary>See submitted information</summary><p>{item.details}</p>{item.links.map((link) => <a key={link} href={link} target="_blank" rel="noreferrer">{link}</a>)}{item.files.map((file) => <a key={file.id} href={`/api/chunkz/intake/${item.id}/files/${file.id}`} target="_blank" rel="noreferrer"><FileText size={15} /> {file.name}</a>)}</details>
      {item.status === "pending" && <><label className="chunkz-intake-more-files">Add PDFs<input type="file" accept="application/pdf,.pdf" multiple disabled={busy} onChange={(event) => { const files = Array.from(event.currentTarget.files || []); event.currentTarget.value = ""; void addFiles(item, files); }} /></label><button type="button" className="button button-ghost" onClick={() => onReview(item)}>Review and publish</button></>}
    </article>)}</div>
  </section>;
}
